import { Component, computed, DestroyRef, inject, input, model, OnInit, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

import { KeyValueType } from '@models/common';
import { FieldDataSourceSystemDictionaries } from '@models/module/fieldDataSourceType';
import { DisplayType, MeasurementSystem, MeasurementType } from '@models/module/fields/enums';
import { AddressModel, AttachmentModel, BaseFieldValueType } from '@models/module/fields/main';
import { Currency } from '@models/system';
import { TenantSettingsEnum } from '@models/tenant/settings';
import { TableFieldConfig } from '@module-definition/table-field/common/table-field.models';
import {
  CipoAddressConfig,
  CipoDateTimePickerType,
  CipoFieldConfig,
  CipoFieldTypes,
  CipoTextViewConfig,
  FieldsService,
} from '@shared/components/fields/common';
import { FroalaConfig } from '@shared/components/froala/common';
import { FroalaComponent } from '@shared/components/froala/froala.component';
import { ItvDatePipe, ItvDateTimePipe, ItvTimePipe } from '@shared/pipes';
import { NotificationService, UtilsService } from '@shared/services';
import { getLinkData } from '@shared/utils';
import { TenantStore } from '@signal-store/tenant.store';
import { UserStore } from '@signal-store/user.store';
import { isNil } from 'lodash';
import { CipoFormModule } from '../../../modules/forms.module';
import { CipoAddressService } from '../cipo-address/cipo-address.service';
import { CipoAttachmentComponent } from '../cipo-attachment/cipo-attachment.component';
import { CipoOutlineFieldComponent } from '../cipo-outline-field/cipo-outline-field.component';

@Component({
  selector: 'cipo-text-view',
  imports: [
    CipoFormModule,
    CipoAttachmentComponent,
    FroalaComponent,
    NgxSkeletonLoaderModule,
    CipoOutlineFieldComponent,
  ],
  templateUrl: './cipo-text-view.component.html',
  styleUrl: './cipo-text-view.component.scss',
})
export class CipoTextViewComponent implements OnInit {
  utilsService = inject(UtilsService);
  tenantStore = inject(TenantStore);
  userStore = inject(UserStore);
  translate = inject(TranslateService);
  notify = inject(NotificationService);
  private fieldsService = inject(FieldsService);
  private destroyRef = inject(DestroyRef);

  private itvDateTimePipe = inject(ItvDateTimePipe);
  private itvDatePipe = inject(ItvDatePipe);
  private itvTimePipe = inject(ItvTimePipe);
  private addressService = inject(CipoAddressService);
  readonly froalaConfig = FroalaConfig;

  value = input<BaseFieldValueType>('-');
  config = model<CipoFieldConfig>({});
  readonlyConfig = input<CipoTextViewConfig>({});

  translationsLoaded = signal(false);

  userSettings = computed(() => this.userStore.calculatedUserSettings());

  readonly CipoFieldTypes = CipoFieldTypes;

  displayValue = computed<BaseFieldValueType>(() => this.transformValue(this.value()));
  displayOldValue = computed<BaseFieldValueType>(() => this.transformValue(this.readonlyConfig()?.oldValue));
  displayNewValue = computed<BaseFieldValueType>(() => this.transformValue(this.readonlyConfig()?.newValue));

  attachments = computed<AttachmentModel[]>(() => {
    const { type, multiple } = this.config();
    if (type !== CipoFieldTypes.Attachment) return [];

    const oldValue = (this.displayOldValue() as AttachmentModel[]) || [];
    const newValue = (this.displayNewValue() as AttachmentModel[]) || [];
    const value = (this.displayValue() as AttachmentModel[]) || [];
    if (multiple) {
      return [...oldValue, ...value, ...newValue].filter(Boolean);
    }
    return [oldValue, value, newValue].filter(
      v => Boolean(v) && (!Array.isArray(v) || v.length > 0),
    ) as unknown as AttachmentModel[];
  });

  allValues = computed(() => this.mapAllValuesToArray());
  hasAnyValue = computed(() => {
    const values = this.allValues();
    return Array.isArray(values) ? values.length > 0 : !!values;
  });
  hasAddressesDataSource = computed(
    () =>
      this.config()?.dataSourceInfo?.id === FieldDataSourceSystemDictionaries.Addresses ||
      this.config()?.dataSourceInfo?.id === FieldDataSourceSystemDictionaries.GlobalAddresses,
  );
  isAddressField = computed(() => this.config()?.type === CipoFieldTypes.Address);
  isSelectWithAddressDataSource = computed(
    () => this.config()?.type === CipoFieldTypes.Select && this.hasAddressesDataSource(),
  );
  clickableAddress = computed(
    () => this.hasAnyValue() && (this.isAddressField() || this.isSelectWithAddressDataSource()),
  );
  clickable = computed(() => this.clickableAddress());

  linkedDisplayValue = computed(() => getLinkData(this.displayValue() ?? this.displayNewValue())?.value);
  hasCopyableDisplayValue = computed(() => this.config()?.allowCopy === true || !!this.linkedDisplayValue());

  ngOnInit() {
    this.fieldsService.checkOptions(this.config, this.value(), this.destroyRef);
  }

  isLinkValue(value: BaseFieldValueType): boolean {
    return !!getLinkData(value);
  }

  getLinkHref(value: BaseFieldValueType): string {
    return getLinkData(value)?.href || '';
  }

  copyDisplayValue(event: MouseEvent) {
    event.stopPropagation();

    const valueToCopy =
      this.linkedDisplayValue() || this.getDisplayValueToCopy(this.displayValue() ?? this.displayNewValue());
    if (!valueToCopy) {
      return;
    }

    navigator.clipboard.writeText(valueToCopy);
    this.notify.success('messages.valueCopied');
  }

  handleClick() {
    if (this.isAddressField()) {
      this.handleAddressClick();
    }

    if (this.isSelectWithAddressDataSource()) {
      this.handleAddressFromSelectClick();
    }
  }

  private getDisplayValueToCopy(value: BaseFieldValueType): string {
    if (isNil(value)) {
      return '';
    }

    if (Array.isArray(value)) {
      return value
        .map((item: BaseFieldValueType) => this.getDisplayValueToCopy(item))
        .filter(item => item && item.trim().length > 0)
        .join(', ');
    }

    if (typeof value === 'object') {
      return '';
    }

    return `${value}`.trim();
  }

  private handleAddressClick() {
    const addresses = this.mapAllValuesToArray<AddressModel>();
    const multiple = addresses.length > 1 || (this.config()?.multiple ?? false);
    const config = { ...this.config(), readonly: true, multiple } as CipoAddressConfig;
    this.addressService.openMap(config, addresses).subscribe();
  }

  private handleAddressFromSelectClick() {
    this.addressService.getAddressAndOpenMap(this.config()?.label ?? '', this.value() as number).subscribe();
  }

  private mapAllValuesToArray<T extends BaseFieldValueType>(): T[] {
    const mappedValue = !this.value() ? [] : Array.isArray(this.value()) ? (this.value() as T[]) : [this.value() as T];
    const oldValue = this.readonlyConfig()?.oldValue;
    const newValue = this.readonlyConfig()?.newValue;
    const mappedOldValue = !oldValue
      ? []
      : Array.isArray(oldValue)
        ? (oldValue as T[])
        : oldValue
          ? [oldValue as T]
          : [];
    const mappedNewValue = !newValue
      ? []
      : Array.isArray(newValue)
        ? (newValue as T[])
        : newValue
          ? [newValue as T]
          : [];
    return [...mappedValue, ...mappedOldValue, ...mappedNewValue].filter(v => !!v) as T[];
  }

  private transformValue(value: BaseFieldValueType): BaseFieldValueType {
    if (isNil(value)) {
      return null;
    }
    const config = this.config() as CipoFieldConfig & TableFieldConfig;
    switch (config.type) {
      case CipoFieldTypes.Text:
        return this.handleText(value as string | string[]);

      case CipoFieldTypes.Number:
        return this.handleNumber(value as number | number[], config);

      case CipoFieldTypes.Select:
      case CipoFieldTypes.Listbox:
      case CipoFieldTypes.RadioGroup:
        const mappedValue = Array.isArray(value) ? value : [value];
        return this.handleSelect(mappedValue as string[], config);

      case CipoFieldTypes.DateTime:
        return this.handleDateTime(value as string, config);

      case CipoFieldTypes.Address:
        return this.handleAddress(value as AddressModel | AddressModel[], config.displayType);

      default:
        return value;
    }
  }

  private handleAddress(value: AddressModel | AddressModel[], displayType?: DisplayType) {
    if (Array.isArray(value)) {
      return value.map(val => this.addressService.formatAddress(val, displayType));
    }
    return this.addressService.formatAddress(value, displayType);
  }

  private handleText(value: string | string[]) {
    const { prefix = '', suffix = '', multiple = false } = this.config();
    if (multiple) {
      const formattedVals = (value as string[]).map(val => prefix + (val || '-') + suffix);
      return formattedVals;
    }

    return prefix + (value || '-') + suffix;
  }

  private handleNumber(value: number | number[], config: CipoFieldConfig & TableFieldConfig) {
    const { multiple, prefix, suffix, decimals, currency, showThousandSeparator, name } = config;
    // this is a temporary solution to handle measurement fields until we transform this into a specific field format type
    if (['weather_temperature', 'weather_wind', 'weather_precipitation'].includes(name)) {
      return this.handleMeasurement(value as number, name);
    }
    if (multiple) {
      const formattedVals = (value as number[]).map(value =>
        this.handleNumberSingle(value, prefix, suffix, decimals, currency, showThousandSeparator),
      );
      return formattedVals;
    }

    return this.handleNumberSingle(value as number, prefix, suffix, decimals, currency, showThousandSeparator);
  }

  private handleNumberSingle(
    value: number,
    prefix: string,
    suffix: string,
    decimals: number,
    currency: Currency,
    showThousandSeparator: boolean,
  ) {
    if (!currency?.symbol && prefix) {
      currency = { symbol: prefix, leftSide: true, noSpace: false } as Currency;
    }
    const formattedVal = this.utilsService.convertValueToNumberOrCurrencyString(
      value,
      decimals,
      !!showThousandSeparator,
      !!currency?.symbol,
      !!suffix,
      currency,
      suffix,
      this.userSettings().decimalSeparator,
      this.userSettings().thousandSeparator,
    );
    return formattedVal;
  }

  private handleMeasurement(value: number, fieldName: string) {
    const measurementType: MeasurementType =
      fieldName === 'weather_wind' ? 'speed' : fieldName === 'weather_temperature' ? 'temperature' : 'precipitation';
    const measurementSystem = this.tenantStore.getSetting(TenantSettingsEnum.measurement_system)
      .calculatedValue as keyof typeof MeasurementSystem;
    return this.utilsService.convertValueToMeasurementString(value, measurementType, measurementSystem);
  }

  private handleSelect(value: string[], config: CipoFieldConfig) {
    const options = (config?.options || []) as KeyValueType[];
    if (options?.length) {
      // TODO: it seems like we can have number values, and the corresponded key as string in the datasource
      const listboxVal = value.map((v: string | number) => v.toString());
      const selectedOptions = options.filter(option => listboxVal.includes(option.key?.toString()));
      const displayValue = selectedOptions.map(option => option.value);
      return displayValue || '-';
    } else {
      return value;
    }
  }

  private handleDateTime(value: string | string[], config: CipoFieldConfig) {
    const { pickerType, selectMode } = config || {};

    if (selectMode === 'range') {
      const dateRange = value as string[];
      const displayValue = dateRange.map(date => this.getDateTimeFormatted(date, pickerType)).join(' - ');
      return displayValue;
    } else {
      const date = value as string;
      return this.getDateTimeFormatted(date, pickerType);
    }
  }

  private getDateTimeFormatted(value: string, pickerType: CipoDateTimePickerType): string {
    switch (pickerType) {
      case 'time':
        return this.itvTimePipe.transform(value, '-');
      case 'date':
        return this.itvDatePipe.transform(value, '-');
      case 'dateTime':
      default:
        return this.itvDateTimePipe.transform(value, '-');
    }
  }
}
